import DeleteIcon from '@mui/icons-material/Delete';
import {Box, Button, Grid, IconButton, Typography} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';
import {Agent, AgentView, SplitMethodType, useAgents, useUserInfo} from '@ozark/common';
import {TextField} from '@ozark/common/components';
import React, {useEffect} from 'react';
import {Controller, DeepPartial, useFieldArray, useFormContext} from 'react-hook-form';

interface AgentResidualSharesProps {
  isAllowedEditShares: boolean;
  isCalculatedSharesVisible: boolean;
  agent?: Agent;
  isDisabled?: boolean;
}

interface ResidualsShareModel {
  agentResidualShares?: {
    agentId: string;
    agentName: string;
    groupId?: string;
    groupName?: string;
    displayedResiduals: number;
    calculatedResiduals: number;
  }[];
}

const getAgentLabel = (
  agent: {firstName?: string; lastName?: string; group?: {name?: string}},
  withGroup: boolean = false
) => `${agent.firstName} ${agent.lastName}${withGroup ? ` (${agent.group?.name})` : ''}`;

export const AgentFormResidualShares: React.FC<AgentResidualSharesProps> = ({
  agent,
  isAllowedEditShares,
  isCalculatedSharesVisible,
  isDisabled,
}) => {
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: {errors},
  } = useFormContext<ResidualsShareModel>();
  const {isErpUser, userGroupId} = useUserInfo();
  const {
    fields: agentResidualSharesFields,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: 'agentResidualShares',
  });

  const {
    documents: {data: agents},
  } = useAgents(!isErpUser ? agent?.group.id : undefined);

  const agentResidualShares = watch('agentResidualShares') || [];

  const alreadySelectedIds = agentResidualShares.map(item => item.agentId) || [];

  // hack for displaying residual shares array controlled by react-hook-form
  // its empty on first render, until interacted
  useEffect(() => {
    if (getValues) {
      const firstResidualShare = getValues('agentResidualShares')?.[0];
      if (firstResidualShare !== undefined) update(0, firstResidualShare);
    }
  }, [getValues, update]);

  const filteredOverrideAgents = React.useMemo(
    () =>
      agents?.filter(a => {
        return (
          (isErpUser || a.group.id === agent?.group.id) &&
          agent?.isActive &&
          a.id !== agent?.id &&
          a.latestScheduleAVersion?.splitMethod === SplitMethodType.direct
        );
      }) || [],
    [agents, agent]
  );

  return (
    <>
      {agent?.latestScheduleAVersion?.splitMethod !== SplitMethodType.direct && (
        <Grid item xs={12}>
          <Typography>{`Override is allowed only when agent's Schedule A calculation method is "Direct Schedule A Calculation"`}</Typography>
        </Grid>
      )}
      {agent?.latestScheduleAVersion?.splitMethod === SplitMethodType.direct && (
        <>
          {agentResidualSharesFields
            .filter(field => field.agentId !== undefined)
            .map((field, index) => {
              if (!isErpUser && field.agentId !== '' && field.groupId !== userGroupId) {
                return null;
              }
              return (
                <React.Fragment key={field.id}>
                  <Grid item xs={7}>
                    <Controller
                      control={control}
                      name={`agentResidualShares.${index}.agentId`}
                      render={({field: agentIdField}) => (
                        <>
                          <Autocomplete<DeepPartial<AgentView>>
                            fullWidth
                            sx={{marginTop: 2}}
                            disabled={!isAllowedEditShares || isDisabled}
                            options={
                              filteredOverrideAgents?.filter(a => {
                                return (
                                  a.id === agentIdField.value || !alreadySelectedIds.includes(a.id)
                                );
                              }) || []
                            }
                            getOptionLabel={a =>
                              getAgentLabel(a, isErpUser || agent.group.id !== a?.group?.id)
                            }
                            value={
                              agents?.find(a => a.id === agentIdField.value) ||
                              (agentIdField.value !== ''
                                ? {
                                    id: agentIdField.value,
                                    firstName: field.agentName?.split(' ')[0],
                                    lastName: field.agentName?.split(' ')[1],
                                    group: {
                                      id: field?.groupId || '',
                                      name: field?.groupName || '',
                                    },
                                  }
                                : null)
                            }
                            onChange={(_, selectedAgent) => {
                              agentIdField.onChange(selectedAgent?.id || '');
                              setValue(
                                `agentResidualShares.${index}.groupId`,
                                selectedAgent?.group?.id || ''
                              );
                              setValue(
                                `agentResidualShares.${index}.groupName`,
                                selectedAgent?.group?.name || ''
                              );
                              setValue(
                                `agentResidualShares.${index}.agentName`,
                                selectedAgent ? getAgentLabel(selectedAgent as AgentView) : ''
                              );
                            }}
                            renderInput={params => (
                              <MuiTextField
                                {...params}
                                label="Agents Shares"
                                error={
                                  Boolean(errors?.agentResidualShares?.[index]?.agentId) ||
                                  Boolean(errors?.agentResidualShares?.[index]?.agentName)
                                }
                                helperText={
                                  errors?.agentResidualShares?.[index]?.agentId?.message ||
                                  errors?.agentResidualShares?.[index]?.agentName?.message
                                }
                              />
                            )}
                          />
                        </>
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      type="number"
                      name={`agentResidualShares.${index}.displayedResiduals`}
                      label="Displayed Residuals %"
                      disabled={!isAllowedEditShares || isDisabled}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  {isCalculatedSharesVisible && (
                    <Grid item xs={2}>
                      <TextField
                        type="number"
                        name={`agentResidualShares.${index}.calculatedResiduals`}
                        label="Calculated Residuals %"
                        disabled={!isAllowedEditShares || isDisabled}
                        control={control}
                        errors={errors}
                      />
                    </Grid>
                  )}

                  {isAllowedEditShares && (
                    <Grid item xs={1}>
                      <IconButton
                        color="error"
                        sx={{marginTop: 3}}
                        onClick={() => remove(index)}
                        disabled={isDisabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  )}
                </React.Fragment>
              );
            })}
          {isAllowedEditShares && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isDisabled}
                  onClick={() =>
                    append({
                      agentId: '',
                      agentName: '',
                      displayedResiduals: 0,
                      calculatedResiduals: 0,
                    })
                  }
                >
                  Add Residual Referral Override
                </Button>
              </Box>
            </Grid>
          )}
        </>
      )}
    </>
  );
};
